import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Clock, FolderOpen, Heart, Loader2, Search, X } from "lucide-react";
import { libraryGlowButtonClass } from "@/features/library/lib/library-ui";
import { cn } from "@/lib/utils";
import { usePickerLibrary } from "../hooks/use-picker-library";
import { useFilePickerStore } from "../store/file-picker-store";
import type { PickerOrigin } from "../types/file-picker-types";
import { PickerAssetCard } from "./PickerAssetCard";
import { PickerUploadTile } from "./PickerUploadTile";

const TYPE_FILTERS = [
  { id: null, labelKey: "filePicker.filters.all" },
  { id: "image", labelKey: "filePicker.filters.images" },
  { id: "video", labelKey: "filePicker.filters.videos" },
  { id: "audio", labelKey: "filePicker.filters.audios" },
  { id: "document", labelKey: "filePicker.filters.documents" },
] as const;

const WARDROBE_CATEGORY_FILTERS = [
  { id: null, labelKey: "filePicker.wardrobeCategories.all" },
  { id: "Topwear", labelKey: "filePicker.wardrobeCategories.topwear" },
  { id: "Bottomwear", labelKey: "filePicker.wardrobeCategories.bottomwear" },
  { id: "Shoes", labelKey: "filePicker.wardrobeCategories.shoes" },
  { id: "Outerwear", labelKey: "filePicker.wardrobeCategories.outerwear" },
  { id: "Accessories", labelKey: "filePicker.wardrobeCategories.accessories" },
  { id: "Underwear", labelKey: "filePicker.wardrobeCategories.underwear" },
  { id: "OnePiece", labelKey: "filePicker.wardrobeCategories.onePiece" },
  { id: "uncategorized", labelKey: "filePicker.wardrobeCategories.uncategorized" },
] as const;

const ORIGINS: { id: PickerOrigin; labelKey: string }[] = [
  { id: "any", labelKey: "filePicker.origin.any" },
  { id: "outputs", labelKey: "filePicker.origin.outputs" },
  { id: "inputs", labelKey: "filePicker.origin.inputs" },
];

const gridClass = "grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3";

const chipClass = (isActive: boolean) =>
  cn(
    libraryGlowButtonClass,
    "shrink-0 h-8 px-3 rounded-full text-xs font-medium whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60",
    isActive
      ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
      : "text-text-secondary dark:text-gray-300 ring-1 ring-inset ring-black/[0.08] dark:ring-white/[0.1] hover:text-text-primary dark:hover:text-white"
  );

const PickerToolbar = () => {
  const { t } = useTranslation();
  const config = useFilePickerStore((state) => state.config);
  const source = useFilePickerStore((state) => state.source);
  const search = useFilePickerStore((state) => state.search);
  const origin = useFilePickerStore((state) => state.origin);
  const typeFilter = useFilePickerStore((state) => state.typeFilter);
  const categoryFilter = useFilePickerStore((state) => state.categoryFilter);
  const setSearch = useFilePickerStore((state) => state.setSearch);
  const setOrigin = useFilePickerStore((state) => state.setOrigin);
  const setTypeFilter = useFilePickerStore((state) => state.setTypeFilter);
  const setCategoryFilter = useFilePickerStore((state) => state.setCategoryFilter);

  const showTypeFilters = !config?.isWardrobe;
  const showCategoryFilters = !!config?.isWardrobe && !config.hideWardrobeCategoryFilters;

  return (
    <div className="shrink-0 flex flex-col gap-2.5 px-3 sm:px-5 pt-3 pb-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-quaternary dark:text-gray-500" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("filePicker.searchLibrary")}
            className="h-10 w-full rounded-xl bg-black/[0.04] dark:bg-white/[0.06] pl-9 pr-9 text-sm text-text-primary dark:text-white placeholder:text-text-quaternary dark:placeholder:text-gray-500 ring-1 ring-inset ring-transparent focus:outline-none focus:ring-violet-500/50 [&::-webkit-search-cancel-button]:hidden"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label={t("common.clear")}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md flex items-center justify-center text-text-tertiary dark:text-gray-400 hover:bg-black/5 dark:hover:bg-white/10"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {source !== "recent" && (
          <div
            role="radiogroup"
            aria-label={t("filePicker.origin.label")}
            className="hidden sm:flex shrink-0 items-center gap-0.5 rounded-xl bg-black/[0.04] dark:bg-white/[0.06] p-1"
          >
            {ORIGINS.map((option) => (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={origin === option.id}
                onClick={() => setOrigin(option.id)}
                className={cn(
                  "h-8 px-3 rounded-lg text-xs font-medium transition-colors",
                  origin === option.id
                    ? "bg-bg-primary dark:bg-gray-800 text-text-primary dark:text-white shadow-sm"
                    : "text-text-tertiary dark:text-gray-400 hover:text-text-primary dark:hover:text-white"
                )}
              >
                {t(option.labelKey)}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="-mx-3 sm:-mx-5 flex items-center gap-1.5 overflow-x-auto overscroll-x-contain px-3 sm:px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {source !== "recent" &&
          ORIGINS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setOrigin(option.id)}
              aria-pressed={origin === option.id}
              className={cn(chipClass(origin === option.id), "sm:hidden")}
            >
              {t(option.labelKey)}
            </button>
          ))}
        {source !== "recent" && showTypeFilters && (
          <span aria-hidden className="sm:hidden mx-1 h-5 w-px shrink-0 bg-black/10 dark:bg-white/10" />
        )}
        {showTypeFilters &&
          TYPE_FILTERS.map((filter) => (
            <button
              key={filter.id ?? "all"}
              type="button"
              onClick={() => setTypeFilter(filter.id)}
              aria-pressed={typeFilter === filter.id}
              className={chipClass(typeFilter === filter.id)}
            >
              {t(filter.labelKey)}
            </button>
          ))}
        {showCategoryFilters &&
          WARDROBE_CATEGORY_FILTERS.map((filter) => (
            <button
              key={filter.id ?? "all"}
              type="button"
              onClick={() => setCategoryFilter(filter.id)}
              aria-pressed={categoryFilter === filter.id}
              className={chipClass(categoryFilter === filter.id)}
            >
              {t(filter.labelKey)}
            </button>
          ))}
      </div>
    </div>
  );
};

const EmptyState = ({ isFiltered }: { isFiltered: boolean }) => {
  const { t } = useTranslation();
  const source = useFilePickerStore((state) => state.source);
  const Icon = source === "recent" ? Clock : source === "favorites" ? Heart : FolderOpen;
  const [titleKey, descriptionKey] = isFiltered
    ? ["filePicker.noResults", "filePicker.noResultsDescription"]
    : source === "recent"
      ? ["filePicker.empty.recentTitle", "filePicker.empty.recentDescription"]
      : source === "favorites"
        ? ["filePicker.empty.favoritesTitle", "filePicker.empty.favoritesDescription"]
        : source === "folder"
          ? ["filePicker.empty.folderTitle", "filePicker.empty.folderDescription"]
          : ["filePicker.emptyLibrary", "filePicker.emptyLibraryDescription"];

  return (
    <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-3 px-6 text-center">
      <div className="w-14 h-14 rounded-2xl bg-black/[0.04] dark:bg-white/[0.06] flex items-center justify-center">
        <Icon className="w-6 h-6 text-text-tertiary dark:text-gray-400" />
      </div>
      <h3 className="text-base font-semibold text-text-primary dark:text-white">{t(titleKey)}</h3>
      <p className="max-w-sm text-sm text-text-tertiary dark:text-gray-400">{t(descriptionKey)}</p>
    </div>
  );
};

/** Seçicinin Library ızgarası. */
export const PickerGrid = () => {
  const { t } = useTranslation();
  const uploads = useFilePickerStore((state) => state.uploads);
  const {
    groups,
    items,
    isRecent,
    isFiltered,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePickerLibrary();
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void fetchNextPage();
      },
      { root: scrollRef.current, rootMargin: "400px" }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, groups]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PickerToolbar />

      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain px-3 sm:px-5 pb-4"
      >
        {uploads.length > 0 && (
          <section className="pt-2">
            <h3 className="pb-3 text-sm font-semibold text-text-primary dark:text-white">
              {t("filePicker.uploading")}
            </h3>
            <div className={gridClass}>
              {uploads.map((upload) => (
                <PickerUploadTile key={upload.id} upload={upload} />
              ))}
            </div>
          </section>
        )}

        {isLoading && (
          <div className={cn(gridClass, "pt-2")}>
            {Array.from({ length: 12 }, (_, index) => (
              <div
                key={index}
                className="aspect-square rounded-2xl animate-pulse bg-bg-tertiary dark:bg-gray-800"
              />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-3 px-6 text-center">
            <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
            <h3 className="text-base font-semibold text-text-primary dark:text-white">
              {t("filePicker.libraryError")}
            </h3>
            <p className="max-w-sm text-sm text-text-tertiary dark:text-gray-400">
              {t("filePicker.libraryErrorDescription")}
            </p>
            <button type="button" onClick={() => void refetch()} className={chipClass(false)}>
              {t("common.retry")}
            </button>
          </div>
        )}

        {!isLoading && !isError && items.length === 0 && uploads.length === 0 && (
          <EmptyState isFiltered={isFiltered} />
        )}

        {!isLoading &&
          !isError &&
          groups.map((group) => (
            <section key={group.key} className="pt-2">
              <div className="flex items-center gap-2 pb-3">
                <h3 className="text-sm font-semibold text-text-primary dark:text-white">
                  {group.label}
                </h3>
                <span className="text-xs tabular-nums text-text-quaternary dark:text-gray-500">
                  {group.items.length}
                </span>
              </div>
              <div className={gridClass}>
                {group.items.map((item) => (
                  <PickerAssetCard key={item._id} item={item} />
                ))}
              </div>
            </section>
          ))}

        {!isRecent && hasNextPage && <div ref={sentinelRef} aria-hidden className="h-1" />}
        {isFetchingNextPage && (
          <div className="flex justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-text-tertiary dark:text-gray-400" />
          </div>
        )}
      </div>
    </div>
  );
};
